import { useDispatch } from 'react-redux';
import { prop } from 'js/utils/ramda.util';
import { useTranslations } from 'js/hooks/i18n';
import { Icon } from 'js/components/common/Icon';
import { useDeviceSelector } from 'js/store/hooks';
import React, { useCallback, useMemo } from 'react';
import { useFormatter } from 'js/hooks/useFormatter';
import { addPopupAction, LAYER } from 'js/components/common/PopupManager';

import { PeriodPopup } from '../PeriodPopup';
import * as helpers from '../../../../helpers';
import * as actions from '../../../../store/actions';
import { useDynamicReducerSelector, useDynamicReducerDispatch } from '../../../../store';

import style from './Inputs.scss';

export const Inputs = (props) => {
    const { period } = props;
    const formatter = useFormatter();
    const dispatchPopup = useDispatch();
    const dispatch = useDynamicReducerDispatch();
    const isMobile = useDeviceSelector(prop('isMobile'));
    const translate = useTranslations('playtech.mobile.system.scheduledBreaks.DateTimePicker.');
    const data = useDynamicReducerSelector(prop('dateTimePicker'));
    const { start, end, periodData } = useMemo(() => ({
            ...helpers.getValuesPerPeriod({
                period,
                data,
                modifyTime: (time) => formatter.getTimeShort(helpers.getParsedDateTime(time)),
                modifyDate: (date) => formatter.getDate(date)
            }),
            periodData: data[period]
        }), [formatter, period, data]);

    const setValues = useCallback(({ form, time }) => {
        dispatch(actions.setData({ form, time, period }));
    }, [dispatch, period]);
    const onClick = useCallback(({ time }) => {
        dispatchPopup(addPopupAction({
            data: { time, period, setValues, formData: periodData[time] },
            layer: LAYER.COMMON,
            component: PeriodPopup
        }));
    }, [dispatchPopup, period, setValues, periodData]);
    const onKeyDown = useCallback((event) => {
        const { key, target: { name } } = event;

        if (key === 'Enter') {
            event.preventDefault();

            name === 'start' && onClick({ time: 'start' });
            name === 'end' && onClick({ time: 'end' });
        }
    }, [onClick]);

    return (
        <form className={style.form}>
            <label className={style.input}>
                <span className={style.label} >{translate(`start${period}.label`)}</span>
                <div className={style.iconContainer}>
                    <input
                        readOnly
                        type="text"
                        name="start"
                        value={start}
                        onKeyDown={onKeyDown}
                        className={style.inputField}
                        onClick={() => onClick({ time: 'start' })}
                        aria-label={translate(`start${period}.label`) || translate('playtech.mobile.system.input.aria-label')}
                    />
                    <Icon type='clock' className={style.icon} />
                </div>
            </label>
            {!isMobile && <span className={style.sep}>{'—'}</span>}
            <label className={style.input}>
                <span className={style.label} >{translate(`end${period}.label`)}</span>
                <div className={style.iconContainer}>
                    <input
                        readOnly
                        name="end"
                        type="text"
                        value={end}
                        onKeyDown={onKeyDown}
                        className={style.inputField}
                        onClick={() => onClick({ time: 'end' })}
                        aria-label={translate(`end${period}.label`) || translate('playtech.mobile.system.input.aria-label')}
                    />
                    <Icon type='clock' className={style.icon} />
                </div>
            </label>
        </form>
    );
};
